/* System defined core library */
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, Observer, Subscription, throwError, forkJoin } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { HttpService } from '../../../core/http.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { PaymentApiService } from '../../shared/services/payment-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { TimerService } from '../../../shared/services/timer.service';
import { MessagingApiService } from './messaging-api.service';
import { SchedulingApiService } from './scheduling-api.service';

/* User defined models */
import { DynamicFormBase } from '../../../shared/models/common/dynamic-form-base.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentDetail.model';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { BookingRequestSchedule } from '../../../shared/models/scheduling/bookingRequest.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { EligibilityDetail, Restriction } from '../../../shared/models/scheduling/eligibility.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';
import { MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { Attribute, TestingAccommodation } from '../../../shared/models/scheduling/testingAccommodation.model';
import { CreatePaymentSessionRequest, CreatePaymentSessionResponse, PaymentOrchestratorRequest, PaymentSession } from '../../../shared/models/scheduling/paymentSession.model';

/* User defined enums/constants */
import { LanguageCultureCodes, SchedulingErrors, TargetFlow } from '../../../shared/constants/scheduling.constant';
import { CardFee, ErrorMessage, LogLevel } from '../../../shared/enums/scheduling.enum';


@Injectable()
export class RegistrationApiService {
  /* User defined private/public variables */
  public bookingResult: any = [];
  public authId: string = '';
  public voucherAuthId: string = '';
  public dynamicInputCtrls: DynamicFormBase<any>[];
  public paymentSessionData: PaymentSession;

  /* Service subscriptions */
  dynamicInputCtrls$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private httpService: HttpService,
    private messagingApiService: MessagingApiService,
    private metricApiService: MetricApiService,
    private paymentService: PaymentApiService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService
  ) {
    this.dynamicInputCtrls$ = selectedExamDetailService.formInputData.subscribe(
      formInputData => {
        this.dynamicInputCtrls = formInputData;
      });
  }

  getEligibilityDetails(programCode: string, eligibilitiesRequestData: any, confirmationNumber?: string, skipSetCall?: boolean): Observable<EligibilityDetail[]> {
    let eligibilityDetails: EligibilityDetail[] = [];
    return new Observable((observer: Observer<any>) => {
      let apiPath = confirmationNumber ? (this.bootstrapParams.RegistrationRootApiUrl + 'api/v1/Eligibilities?programcode=' + programCode + '&' + eligibilitiesRequestData + '&confirmationNumber=' + confirmationNumber) :
        (this.bootstrapParams.RegistrationRootApiUrl + 'api/v1/Eligibilities?programcode=' + programCode + '&' + eligibilitiesRequestData + '&scheduleMethod=Internet');

      this.httpService.get(apiPath, this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (eligibilities: any[]) => {

            if (eligibilities == undefined || eligibilities == null) { // In case of No content resposne from API. //TODO : for now error message is showing untill will get proper message for that
              let exam: Exam = new Exam("-1", "Error", "httpError", "Error Occured", false, false);
              let eligibilityDetail: EligibilityDetail = { id: "-1", href: "httpError", number: "0000", exam: exam };
              eligibilityDetails.push(eligibilityDetail)
              observer.next(eligibilityDetails);
              observer.complete();
            }
            else {

              if (!confirmationNumber) {
                eligibilities = eligibilities.filter((elig: EligibilityDetail) => {
                  return elig.status !== 'used';
                });

                if (!eligibilities.length) {
                  let exam: Exam = new Exam("-1", "EligibilityUsedError", "httpError", "Error Occured", false, false);
                  let eligibilityDetail: EligibilityDetail = { id: "-1", href: "EligibilityUsedError", number: "0000", exam: exam };
                  eligibilityDetails.push(eligibilityDetail)
                  observer.next(eligibilityDetails);
                  observer.complete();
                }
              }

              // Sorting apply on eligibilities
              eligibilities = this.applyEligibilitySort(eligibilities);

              eligibilities = eligibilities.filter(elig => moment(elig.restrictions.endDate).valueOf() > moment().valueOf());

              if (!eligibilities.length) {
                let exam: Exam = new Exam("-1", "EligibilityExpiredError", "httpError", "Error Occured", false, false);
                let eligibilityDetail: EligibilityDetail = { id: "-1", href: "EligibilityExpiredError", number: "0000", exam: exam };
                eligibilityDetails.push(eligibilityDetail)
                observer.next(eligibilityDetails);
                observer.complete();
              }

              let invalidExamsOnEligibility = 0;
              eligibilities.forEach((eligibility: any) => {
                let tempEligibilityDetail: EligibilityDetail = new EligibilityDetail();

                tempEligibilityDetail.id = eligibility.id;
                tempEligibilityDetail.href = eligibility.href;
                tempEligibilityDetail.number = eligibility.number;
                tempEligibilityDetail.programCode = eligibility.programCode;
                tempEligibilityDetail.clientCode = eligibility.clientCode;
                tempEligibilityDetail.status = eligibility.status;
                tempEligibilityDetail.consecutiveDays = eligibility.consecutiveDays;
                tempEligibilityDetail.demographics = eligibility.demographics;
                tempEligibilityDetail.maxDays = eligibility.maxDays > 0 ? eligibility.maxDays : 0;

                if (eligibility.sessions && eligibility.sessions.length > 1 && eligibility.maxDays == 0) {
                  tempEligibilityDetail.consecutiveDays = true;
                }

                this.selectedExamDetailService.maxDays = eligibility.maxDays;
                this.selectedExamDetailService.consecutiveDays = eligibility.consecutiveDays;

                tempEligibilityDetail.exam = new Exam(eligibility.exam.id, "", eligibility.exam.href, "temp", false, false);

                if (!!eligibility.restrictions) {
                  tempEligibilityDetail.restriction = new Restriction(
                    eligibility.restrictions.startDate,
                    eligibility.restrictions.endDate,
                  );
                }

                tempEligibilityDetail.testingAccommodations = [];

                let attributes: Attribute[] = [];

                if (eligibility.testingAccommodations != null && eligibility.testingAccommodations != undefined) {
                  eligibility.testingAccommodations.forEach((testAccommodation: any) => {

                    if (!!testAccommodation.attributes) {
                      testAccommodation.attributes.forEach((attribute: any) => {
                        let tempAttribute: Attribute = new Attribute(attribute.name, attribute.parameters);
                        attributes.push(tempAttribute);
                      });
                    }

                    let tempTestingAccommodation = new TestingAccommodation(
                      testAccommodation.name,
                      attributes
                    );
                    tempEligibilityDetail.testingAccommodations.push(tempTestingAccommodation);
                  });
                }
                //TODO: remove when we implement sessions on all exams
                if (eligibility.sessions.length > 1) {
                  tempEligibilityDetail.sessions = eligibility.sessions;
                }
                this.httpService.get(tempEligibilityDetail.exam.uri + '/?isPublished=true', this.bootstrapParams.Token)
                  .pipe(map(this.getFirstOrDefault))
                  .subscribe({
                    next: (examResult) => {

                      let startDate = (eligibility.restrictions != undefined) ? eligibility.restrictions.startDate : null;
                      let endDate = (eligibility.restrictions != undefined) ? eligibility.restrictions.endDate : null;

                      tempEligibilityDetail.exam = Exam.convertAPIObjectToExam(
                        examResult,
                        tempEligibilityDetail.sessions,
                        tempEligibilityDetail.status,
                        tempEligibilityDetail.testingAccommodations,
                        false,
                        startDate,
                        endDate
                      );
                      if (this.selectedExamDetailService.currentTargetFlow !== TargetFlow.Schedule && !skipSetCall) {
                        // Exam Set Call to check b2b for each exams for eligibility
                        this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/exams/' + tempEligibilityDetail.exam.id + '/set/', this.bootstrapParams.Token)
                          .pipe(map(this.getFirstOrDefault))
                          .subscribe({
                            next: (examSet: any) => {
                              this.selectedExamDetailService.examSetId = examSet.id;
                              if (examSet.attributes && examSet.attributes.b2b != null && examSet.attributes.b2b != undefined) {
                                tempEligibilityDetail.b2b = true;
                              }
                              if (examSet.attributes) {
                                if (!!examSet.attributes.b2b) {
                                  tempEligibilityDetail.b2b = true;
                                  this.selectedExamDetailService.isBackToBackRequired = true;
                                  if (examSet.attributes.b2b.bufferTime > 0) {
                                    this.selectedExamDetailService.setBatteryConfig(new BatteryConfig(
                                      examSet.attributes.b2b.bufferTime,
                                      examSet.attributes.b2b.maxDuration
                                    ));
                                  }
                                }
                                if (examSet.attributes.requiredSchedule) {
                                  tempEligibilityDetail.scheduleSetAttributeCode = true;
                                  this.selectedExamDetailService.scheduleSetAttributeCode = true;
                                }
                                if (examSet.attributes.requiredReSchedule) {
                                  tempEligibilityDetail.rescheduleSetAttributeCode = true;
                                  this.selectedExamDetailService.rescheduleSetAttributeCode = true;
                                }
                                if (examSet.attributes.requiredCancel) {
                                  tempEligibilityDetail.cancelSetAttributeCode = true;
                                  this.selectedExamDetailService.cancelSetAttributeCode = true;
                                }
                              }

                              eligibilityDetails.push(tempEligibilityDetail);


                              if (!eligibilityDetails.length) {
                                let exam: Exam = new Exam("-1", "EligibilityError", "httpError", "Error Occured", false, false);
                                let eligibilityDetail: EligibilityDetail = { id: "-1", href: "httpError", number: "0000", exam: exam };
                                eligibilityDetails.push(eligibilityDetail);
                                observer.next(eligibilityDetails);
                                observer.complete();
                              } else if (eligibilities.length === eligibilityDetails.length + invalidExamsOnEligibility) {

                                if (!!this.selectedExamDetailService.lpiExamCode) {
                                  eligibilityDetails = eligibilityDetails.filter(ed => ed.exam.code.toLowerCase() === this.selectedExamDetailService.lpiExamCode.toLowerCase());
                                }

                                observer.next(this.applyEligibilitySort(eligibilityDetails));
                                observer.complete();
                              }
                            },
                            error: (err) => {
                              tempEligibilityDetail.b2b = false;
                              eligibilityDetails.push(tempEligibilityDetail);


                              if (!eligibilityDetails.length) {
                                let exam: Exam = new Exam("-1", "EligibilityError", "httpError", "Error Occured", false, false);
                                let eligibilityDetail: EligibilityDetail = { id: "-1", href: "httpError", number: "0000", exam: exam };
                                eligibilityDetails.push(eligibilityDetail);
                                observer.next(eligibilityDetails);
                                observer.complete();
                              } else if (eligibilities.length === eligibilityDetails.length + invalidExamsOnEligibility) {

                                if (!!this.selectedExamDetailService.lpiExamCode) {
                                  eligibilityDetails = eligibilityDetails.filter(ed => ed.exam.code.toLowerCase() === this.selectedExamDetailService.lpiExamCode.toLowerCase());
                                }

                                observer.next(this.applyEligibilitySort(eligibilityDetails));
                                observer.complete();
                              }
                            }
                          });
                      } else {
                        eligibilityDetails.push(tempEligibilityDetail);

                        if (!eligibilityDetails.length) {
                          let exam: Exam = new Exam("-1", "EligibilityError", "httpError", "Error Occured", false, false);
                          let eligibilityDetail: EligibilityDetail = { id: "-1", href: "httpError", number: "0000", exam: exam };
                          eligibilityDetails.push(eligibilityDetail);
                          observer.next(eligibilityDetails);
                          observer.complete();
                        } else if (eligibilities.length === eligibilityDetails.length + invalidExamsOnEligibility) {

                          if (!!this.selectedExamDetailService.lpiExamCode) {
                            eligibilityDetails = eligibilityDetails.filter(ed => ed.exam.code.toLowerCase() === this.selectedExamDetailService.lpiExamCode.toLowerCase());
                          }

                          observer.next(this.applyEligibilitySort(eligibilityDetails));
                          observer.complete();
                        }
                      }
                    },
                    error: (err) => {
                      invalidExamsOnEligibility++;
                      if (eligibilities.length === invalidExamsOnEligibility) {
                        let exam: Exam = new Exam("-1", "EligibilityError", "httpError", "Error Occured", false, false);
                        let eligibilityDetail: EligibilityDetail = { id: "-1", href: "httpError", number: "0000", exam: exam };
                        eligibilityDetails.push(eligibilityDetail);
                        observer.next(eligibilityDetails);
                        observer.complete();

                      } else if (eligibilities.length === eligibilityDetails.length + invalidExamsOnEligibility) {

                        if (!!this.selectedExamDetailService.lpiExamCode) {
                          eligibilityDetails = eligibilityDetails.filter(ed => ed.exam.code.toLowerCase() === this.selectedExamDetailService.lpiExamCode.toLowerCase());
                        }

                        observer.next(this.applyEligibilitySort(eligibilityDetails));
                        observer.complete();
                      }
                    }
                  });
              });
            }
          },
          error: (err) => {
            const error = err.error;
            const errorCodes = error && error.ModelState ? Object.keys(error.ModelState) : [];
            const errorCode = errorCodes && errorCodes.length ? errorCodes[0] : 'EligibilityError';

            //Need To Implement Global Error Handling (Remove Code Below)
            let exam: Exam = new Exam("-1", ErrorMessage[errorCode], "httpError", "Error Occured", false, false);
            let eligibilityDetail: EligibilityDetail = { id: "-1", href: "httpError", number: "0000", exam: exam };
            eligibilityDetails = [];
            eligibilityDetails.push(eligibilityDetail)
            observer.next(eligibilityDetails);
            observer.complete();
          }
        });
    });
  }

  postBookingDetails(bookingPostRequestData: any, candidate: Candidate, metricDetails?: MetricDetails[], isBackToBackRequired?: boolean, candidateGuid?: string): Observable<any> {

    return new Observable((observer: Observer<any>) => {
      let candidateRequestData: Candidate = new Candidate();
      candidateRequestData.Values = [];

      LanguageCultureCodes.forEach((lang) => {
        if (lang.key === this.selectedExamDetailService.selectedLanguageCode) {
          bookingPostRequestData['culture'] = lang.culture;
        };
      });

      //Using Binding Model to pass values in POST Candidate Method
      candidateRequestData = this.getCandidateRequestData(candidate);

      // TODO: Convert this into FlatMap in order to create just one observable.
      if (bookingPostRequestData['tests'][0] && bookingPostRequestData['tests'][0]["sessions"][0].hold.id) {
        let msgResp = {
          message: "schedulePaymentComponent.errorMessage",
          status: false,
          confirmationNumber: "Error", response: ""
        };
        this.schedulingApiService.validateHold(bookingPostRequestData['tests'][0]["sessions"][0].hold.id).subscribe((response) => {
          if (response) {

            if (this.selectedExamDetailService.lpiCandidateGuid) {
              this.updateCandidate(this.selectedExamDetailService.lpiCandidateGuid, candidateRequestData)
                .pipe(mergeMap((guid) => {
                  if (guid) {
                    bookingPostRequestData.candidate.id = guid;
                    return this.createBooking(bookingPostRequestData)
                  }
                }),
                catchError(() => {
                  return throwError(() => new Error('Candidate Error'));
                }))
                .subscribe({
                  next: (bookingResponse) => {
                    if (bookingResponse.id) {
                      let bookingId: any = bookingResponse.id;
                      if (bookingResponse.tests.length > 0) {
                        this.bookingResult = bookingResponse;
                        if (bookingResponse.tests[0].sessions[0] != null && bookingResponse.tests[0].sessions[0] != undefined) {

                          if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                            this.postConsents(bookingResponse.tests).subscribe(() => { });
                          }

                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                          }
                        }
                      }
                      observer.next(bookingResponse);
                      observer.complete();
                    } else {
                      observer.next(msgResp);
                      observer.complete();
                    }
                  },
                  error: (err) => {
                    observer.next(msgResp);
                    observer.complete();
                  }
                });
            } else if (!!candidateGuid) {
              bookingPostRequestData.candidate.id = candidateGuid;
              this.createBooking(bookingPostRequestData)
                .subscribe({
                  next: (response) => {
                    if (response.id) {
                      let bookingId: any = response.id;
                      if (response.tests.length > 0) {
                        this.bookingResult = response;
                        if (response.tests[0].sessions[0] != null && response.tests[0].sessions[0] != undefined) {

                          if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                            this.postConsents(response.tests).subscribe(() => { });
                          }

                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                          }
                        }
                      }
                      observer.next(response);
                      observer.complete();
                    } else {
                      observer.next(msgResp);
                      observer.complete();
                    }
                  },
                  error: () => {
                    observer.next(msgResp);
                    observer.complete();
                  }
                });
            } else {
              this.createCandidate(candidateRequestData)
                .pipe(mergeMap((guid) => {
                  if (guid) {
                    bookingPostRequestData.candidate.id = guid;
                    return this.createBooking(bookingPostRequestData);
                  }
                }),
                  catchError(() => {
                    return throwError(() => new Error('Candidate Error'));
                  }))
                .subscribe({
                  next: (response) => {
                    if (response.id) {
                      let bookingId: any = response.id;
                      if (response.tests.length > 0) {
                        this.bookingResult = response;
                        if (response.tests[0].sessions[0] != null && response.tests[0].sessions[0] != undefined) {

                          if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                            this.postConsents(response.tests).subscribe(() => { });
                          }

                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                          }
                        }
                      }
                      observer.next(response);
                      observer.complete();
                    } else {
                      observer.next(msgResp);
                      observer.complete();
                    }
                  },
                  error: () => {
                    observer.next(msgResp);
                    observer.complete();
                  }
                });
            }
          } else {
            const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
            this.metricApiService.log(LogLevel.Error, `Validate hold call returned expired when checking for hold ${holdId}`);
            this.timerService.expireTimer();
          }
        });
      }
    });
  }

  prepareBookingPost(
    paymentDetails: CreatePaymentSessionRequest,
    bookingPostRequestData: any,
    candidate: Candidate,
    candidateGuid?: string
  ): Observable<any> {

    return new Observable((observer: Observer<any>) => {
      let candidateRequestData: Candidate = new Candidate();
      candidateRequestData.Values = [];
      // Using Binding Model to pass values in POST Candidate Method
      candidateRequestData = this.getCandidateRequestData(candidate);

      if (bookingPostRequestData['tests'][0] && bookingPostRequestData['tests'][0]["sessions"][0].hold.id) {
        // validate hold
        let validatededHolds$: Observable<boolean>[] = [];

        bookingPostRequestData['tests'][0]['sessions'].forEach((session: any) => {
          validatededHolds$.push(this.schedulingApiService.validateHold(session.hold.id));
        });

        forkJoin(validatededHolds$).subscribe((response) => {
          if (!!response && !!response.length && response.every(v => !!v)) {
            let bookingRequestData = {
              "candidate": {
                "id": ''
              },
              "tests": bookingPostRequestData.tests,
              "payments": bookingPostRequestData.payments,
            }

            let msgResp = {
              message: "errors.thereWasAnIssue",
              status: false,
              confirmationNumber: "Error",
              response: "",
              errorId: SchedulingErrors.Default
            };

            LanguageCultureCodes.forEach((lang) => {
              if (lang.key === this.selectedExamDetailService.selectedLanguageCode) {
                bookingRequestData['culture'] = lang.culture;
              };
            });

            if (bookingPostRequestData.eligibility) {
              bookingRequestData['eligibility'] = bookingPostRequestData.eligibility
            }

            if (this.selectedExamDetailService.lpiCandidateGuid) {
              // update candidate
              this.updateCandidate(this.selectedExamDetailService.lpiCandidateGuid, candidateRequestData)
                .pipe(mergeMap((guid) => {
                  this.selectedExamDetailService.setCandidateGuid(guid);
                  bookingRequestData['candidate']['id'] = guid;

                  // payment orchestrator init
                  return this.paymentService.createSession(paymentDetails);
                }),
                catchError((err) => {
                  if (err.message === 'Orchestrator unreachable') {
                    return throwError(() => new Error('Payment Orchestrator Communication Error'));
                  }
                  return throwError(() => new Error('Candidate Error'));
                }),
                mergeMap((orchestratorResponse: CreatePaymentSessionResponse) => {
                  if (!!orchestratorResponse && !!orchestratorResponse.paymentOrchestratorId) {
                    this.selectedExamDetailService.setPaymentOrchestratorSessionDetail(orchestratorResponse);
                  } else {
                    return throwError(() => new Error('Payment Orchestrator Error'));
                  }
                  // extend hold
                  let extendedHolds$: Observable<boolean>[] = [];

                  bookingPostRequestData['tests'][0]['sessions'].forEach((session: any) => {
                    extendedHolds$.push(this.schedulingApiService.extendHold(session.hold.id));
                  });

                  return forkJoin(extendedHolds$);
                }),
                catchError((err) => {
                  if (err.message === 'Payment Orchestrator Communication Error' || err.message === 'Candidate Error') {
                    return throwError(() => err);
                  }
                  return throwError(() => new Error('Payment Orchestrator Error'));
                }))
                .subscribe({
                  next: (extendResponse: boolean[]) => {
                    if (!extendResponse || !extendResponse.length) {
                      return throwError(() => new Error('Hold Extension Error'));
                    }

                    this.selectedExamDetailService.setBookingRequestData(bookingRequestData);

                    msgResp = {
                      message: "",
                      status: true,
                      confirmationNumber: "",
                      response: "",
                      errorId: ""
                    };
                    observer.next(msgResp);
                    observer.complete();
                  },
                  error: (err) => {
                    if (err.message === 'Candidate Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.CandidateCreationError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Payment Orchestrator Communication Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorCommunicationError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Payment Orchestrator Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Hold Extension Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.HoldError
                      };
                      observer.next(msgResp);
                    } else {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.Default
                      };
                      observer.next(msgResp);
                    }

                    observer.complete();
                  }
                });
            } else if (!!candidateGuid) {
              bookingRequestData['candidate']['id'] = candidateGuid;

              // payment orchestrator init
              this.paymentService.createSession(paymentDetails)
                .pipe(mergeMap((orchestratorResponse: CreatePaymentSessionResponse) => {
                  if (!!orchestratorResponse && !!orchestratorResponse.paymentOrchestratorId) {
                    this.selectedExamDetailService.setPaymentOrchestratorSessionDetail(orchestratorResponse);
                  } else {
                    return throwError(() => new Error('Payment Orchestrator Error'));
                  }
                  // extend hold
                  let extendedHolds$: Observable<boolean>[] = [];

                  bookingPostRequestData['tests'][0]['sessions'].forEach((session: any) => {
                    extendedHolds$.push(this.schedulingApiService.extendHold(session.hold.id));
                  });

                  return forkJoin(extendedHolds$);
                }),
                catchError((err) => {
                  if (err.message === 'Orchestrator unreachable') {
                    return throwError(() => new Error('Payment Orchestrator Communication Error'));
                  }
                  return throwError(() => new Error('Payment Orchestrator Error'));
                }))
                .subscribe({
                  next: (extendResponse: boolean[]) => {
                    if (!extendResponse || !extendResponse.length) {
                      return throwError(() => new Error('Hold Extension Error'));
                    }

                    this.selectedExamDetailService.setBookingRequestData(bookingRequestData);

                    msgResp = {
                      message: "",
                      status: true,
                      confirmationNumber: "",
                      response: "",
                      errorId: ""
                    };
                    observer.next(msgResp);
                    observer.complete();
                  },
                  error: (err) => {
                    if (err.message === 'Candidate Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.CandidateCreationError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Payment Orchestrator Communication Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorCommunicationError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Payment Orchestrator Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Hold Extension Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.HoldError
                      };
                      observer.next(msgResp);
                    } else {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.Default
                      };
                      observer.next(msgResp);
                    }

                    observer.complete();
                  }
                });
            } else {
              // create candidate
              this.createCandidate(candidateRequestData)
                .pipe(mergeMap((guid) => {
                  this.selectedExamDetailService.setCandidateGuid(guid);
                  bookingRequestData['candidate']['id'] = guid;

                  if (guid) {
                    return this.paymentService.createSession(paymentDetails);
                  }
                }),
                catchError((err) => {
                  if (err.message === 'Orchestrator unreachable') {
                    return throwError(() => new Error('Payment Orchestrator Communication Error'));
                  }
                  return throwError(() => new Error('Candidate Error'));
                }),
                mergeMap((orchestratorResponse: CreatePaymentSessionResponse) => {
                  if (!!orchestratorResponse && !!orchestratorResponse.paymentOrchestratorId) {
                    this.selectedExamDetailService.setPaymentOrchestratorSessionDetail(orchestratorResponse);
                  } else {
                    return throwError(() => new Error('Payment Orchestrator Error'));
                  }
                  if (!!bookingRequestData['candidate']['id']) {
                    let extendedHolds$: Observable<boolean>[] = [];

                    bookingPostRequestData['tests'][0]['sessions'].forEach((session: any) => {
                      extendedHolds$.push(this.schedulingApiService.extendHold(session.hold.id));
                    });

                    return forkJoin(extendedHolds$);
                  }
                }),
                catchError((err) => {
                  if (err.message === 'Payment Orchestrator Communication Error' || err.message === 'Candidate Error') {
                    return throwError(() => err);
                  }
                  return throwError(() => new Error('Payment Orchestrator Error'));
                }))
                .subscribe({
                  next: (extendResponse: boolean[]) => {
                    if (!extendResponse || !extendResponse.length) {
                      return throwError(() => new Error('Hold Extension Error'));
                    }

                    this.selectedExamDetailService.setBookingRequestData(bookingRequestData);

                    msgResp = {
                      message: "",
                      status: true,
                      confirmationNumber: "",
                      response: "",
                      errorId: ""
                    };
                    observer.next(msgResp);
                    observer.complete();
                  },
                  error: (err) => {
                    if (err.message === 'Candidate Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.CandidateCreationError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Payment Orchestrator Communication Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorCommunicationError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Payment Orchestrator Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorError
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Hold Extension Error') {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.HoldError
                      };
                      observer.next(msgResp);
                    } else {
                      msgResp = {
                        message: "errors.thereWasAnIssue",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.Default
                      };
                      observer.next(msgResp);
                    }

                    observer.complete();
                  }
                });
            }
          } else {
            const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
            this.metricApiService.log(LogLevel.Error, `Validate hold call returned expired when checking for hold ${holdId}`);
            let msgResp = {
              message: "errors.thereWasAnIssue",
              status: false,
              confirmationNumber: "Error",
              response: "",
              errorId: SchedulingErrors.HoldError
            };
            observer.next(msgResp);
            observer.complete();
          }
        });
      }
    });
  }

  completeBookingPost(
    bookingRequestData: any,
    paymentSessionDetails: CreatePaymentSessionResponse,
    examDetails: any[],
    metricDetails?: MetricDetails[],
    voucherDetail?: any
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.voucherAuthId = '';
      this.paymentSessionData = null;

      let voucherDetails: any;
      if (voucherDetail) {
        voucherDetails = {
          "card": voucherDetail['card'],
          "sessions": voucherDetail['sessions'],
          "candidateId": bookingRequestData['candidate']['id'],
          "type": 'Fee',
          "voucherTransactionId": voucherDetail['voucherTransactionId']
        };
      }

      let msgResp = {
        message: "errors.thereWasAnIssue",
        status: false,
        confirmationNumber: "Error",
        response: "",
        errorId: SchedulingErrors.Default
      };

      const hold = bookingRequestData['tests'][0]['sessions'][0].hold;

      // validate hold
      const validatedHolds$: Observable<boolean>[] = [];

      bookingRequestData['tests'][0]['sessions'].forEach((session: any) => {
        validatedHolds$.push(this.schedulingApiService.validateHold(session.hold.id));
      });

      forkJoin(validatedHolds$)
        .pipe(mergeMap((validateResponse: boolean[]) => {
          if (!!validateResponse && !!validateResponse.length && validateResponse.every(v => !!v)) {
            // verify payment
            return this.paymentService.verifyPayment(paymentSessionDetails.paymentOrchestratorId, hold.id)
          } else {
            return throwError(() => new Error('Validate Hold Error'));
          }
        }),
        catchError((err) => {
          if (err.message === 'Validate Hold Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Payment Verification Error'));
        }),
        mergeMap((verifyResponse: PaymentSession) => {
          if (!!verifyResponse) {
            this.paymentSessionData = verifyResponse;
            this.paymentSessionData['accountId'] = paymentSessionDetails.accountId;
            // authorize voucher
            return this.authorizePayment(voucherDetails);
          }

          return throwError(() => new Error('Payment Verification Error'));
        }),
        catchError((err) => {
          if (err.message === 'Validate Hold Error' || err.message === 'Payment Verification Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Payment Authorization Error'));
        }),
        mergeMap((authResponse: any) => {
          if (authResponse.isRequired && authResponse.isAuthorized) {
            bookingRequestData.payments.push(authResponse.transactionId);
            this.voucherAuthId = authResponse.transactionId;
          } else if (authResponse.isRequired && !authResponse.isAuthorized) {
            return throwError(() => new Error('Voucher Authorization Error'));
          }

          // stage payment
          return this.paymentService.stageSessionPayment(this.paymentSessionData, CardFee.Schedule, examDetails, hold.id);
        }),
        catchError((err) => {
          if (err.message === 'Payment Verification Error' || err.message === 'Validate Hold Error' || err.message === 'Voucher Authorization Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Stage Payment Error'));
        }),
        mergeMap((stageResponse: any) => {
          if (!!stageResponse) {
            bookingRequestData.payments.push(this.paymentSessionData.paymentOrchestratorId);

            // create booking
            return this.createBooking(bookingRequestData);
          }

          return throwError(() => new Error('Stage Payment Error'));
        }),
        catchError((err) => {
          if (err.message === 'Payment Verification Error' || err.message === 'Voucher Authorization Error' || err.message === 'Stage Payment Error' || err.message === 'Validate Hold Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Booking Error'));
        }))
        .subscribe({
          next: (response: any) => {
            if (response.id) {
              const bookingId: any = response.id;
              if (response.tests.length > 0) {
                this.bookingResult = response;
                if (response.tests[0].sessions[0] != null && response.tests[0].sessions[0] !== undefined) {
                  msgResp = {
                    message: "confirmationComponent.confirmationText",
                    status: true,
                    confirmationNumber: response.tests[0].sessions[0].confirmationNumber,
                    response: response,
                    errorId: ""
                  };

                  if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                    this.postConsents(response.tests).subscribe(() => { });
                  }


                  if (metricDetails && metricDetails.length) {
                    this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                  }
                }
              }
              observer.next(msgResp);
              observer.complete();
            } else {
              throwError(() => new Error('Booking Error'));
            }
          },
          error: (err) => {
            if (err.message === 'Validate Hold Error') {
              msgResp = {
                message: "errors.thereWasAnIssue",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.HoldError
              };
              observer.next(msgResp);
            } else if (err.message === 'Payment Verification Error') {
              msgResp = {
                message: "errors.thereWasAnIssue",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.OrchestratorError
              };
              observer.next(msgResp);
            } else if (err.message === 'Voucher Authorization Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.VoucherAuthError
              };
              observer.next(msgResp);
            } else if (err.message === 'Stage Payment Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.StagingError
              };
              observer.next(msgResp);
            } else if (err.message === 'Booking Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.BookingError
              };
              observer.next(msgResp);
            } else {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.Default
              };
              observer.next(msgResp);
            }

            this.metricApiService.log(LogLevel.Error, `Complete schedule ${err} call failure` + `\nHold: ${hold.id}`);

            if (!!this.voucherAuthId) {
              this.metricApiService.log(LogLevel.Debug, `Rolling back voucher transaction due to ${err}\nHold: ${hold.id}`);
              this.paymentService.rollBackTransaction(this.voucherAuthId).subscribe((resp: any) => {
                if (resp != undefined && resp != null) {
                  if (!resp.isCancelled) {
                    msgResp = {
                      message: "errors.thereWasAnIssueRefund",
                      status: false,
                      confirmationNumber: "Error",
                      response: "",
                      errorId: SchedulingErrors.RollbackError
                    };
                    observer.next(msgResp);
                  }
                }
              });
            }
            if (!!this.paymentSessionData && err !== 'Payment Verification Error') {
              this.metricApiService.log(LogLevel.Debug, `Rolling back payment transaction due to ${err}\nHold: ${hold.id}`);
              this.paymentService.rollBackSessionTransaction(this.paymentSessionData.paymentOrchestratorId, this.paymentSessionData.cost)
                .subscribe({
                  next: (resp: any) => {
                    if (resp != undefined && resp != null) {
                      if (!resp.paymentOrchestratorRefundId) {
                        this.metricApiService.log(LogLevel.Error, `Issue rolling back payment.\nHold: ${hold.id}\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                        msgResp = {
                          message: "errors.thereWasAnIssueRefund",
                          status: false,
                          confirmationNumber: "Error",
                          response: "",
                          errorId: SchedulingErrors.RollbackError
                        };
                        observer.next(msgResp);
                      }
                    }
                  },
                  error: (err) => {
                    if (err === 'Transaction Used') {
                      observer.next(msgResp);
                    } else if (err === 'Orchestrator unreachable') {
                      this.metricApiService.log(LogLevel.Error, `Unable to roll back payment due to 429.\nHold: ${hold.id}\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                      msgResp = {
                        message: "errors.thereWasAnIssueRefund",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.OrchestratorCommunicationError
                      };
                      observer.next(msgResp);
                    } else {
                      this.metricApiService.log(LogLevel.Error, `Issue rolling back payment.\nHold: ${hold.id}\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                      msgResp = {
                        message: "errors.thereWasAnIssueRefund",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.RollbackError
                      };
                      observer.next(msgResp);
                    }
                  }
                });
            }

            if (err !== 'Validate Hold Error') {
              bookingRequestData['tests'][0]['sessions'].forEach((session: any) => {
                this.schedulingApiService.releaseHold(session.hold).subscribe(() => { });
              });
            }

            observer.complete();
          }
        });
    });
  }

  postBookingDetailsWithPayment(
    cardDetail: any,
    bookingPostRequestData: any,
    candidate: Candidate,
    metricDetails?: MetricDetails[],
    isBackToBackRequired?: boolean,
    selectedAppts?: AppointmentDetail[],
    candidateGuid?: string,
    voucherDetail?: any
  ): Observable<any> {
    this.authId = '';
    this.voucherAuthId = '';

    return new Observable((observer: Observer<any>) => {
      let candidateRequestData: Candidate = new Candidate();
      candidateRequestData.Values = [];
      // Using Binding Model to pass values in POST Candidate Method
      candidateRequestData = this.getCandidateRequestData(candidate);
      let cardDetails: any;
      if (cardDetail) {
        cardDetails = {
          'card': cardDetail['card'],
          'sessions': cardDetail['sessions'],
          'candidateId': '',
          'type': CardFee.Schedule,
          'siteCountry': cardDetail['siteCountry']
        };
      }
      let voucherDetails: any;
      if (voucherDetail) {
        voucherDetails = {
          "card": voucherDetail['card'],
          "sessions": voucherDetail['sessions'],
          "candidateId": '',
          "type": 'Fee',
          "voucherTransactionId": voucherDetail['voucherTransactionId']
        };
      }

      if (bookingPostRequestData['tests'][0] && bookingPostRequestData['tests'][0]["sessions"][0].hold.id) {
        this.schedulingApiService.validateHold(bookingPostRequestData['tests'][0]["sessions"][0].hold.id).subscribe((response) => {
          if (response) {

            let bookingRequestData = {
              "candidate": {
                "id": ''
              },
              "tests": bookingPostRequestData.tests,
              "payments": bookingPostRequestData.payments,
            }

            let msgResp = {
              message: "schedulePaymentComponent.errorMessage",
              status: false,
              confirmationNumber: "Error", response: ""
            };

            LanguageCultureCodes.forEach((lang) => {
              if (lang.key === this.selectedExamDetailService.selectedLanguageCode) {
                bookingRequestData['culture'] = lang.culture;
              };
            });

            if (bookingPostRequestData.eligibility) {
              bookingRequestData['eligibility'] = bookingPostRequestData.eligibility
            }

            if (this.selectedExamDetailService.lpiCandidateGuid) {
              this.updateCandidate(this.selectedExamDetailService.lpiCandidateGuid, candidateRequestData)
                .pipe(mergeMap((guid) => {
                  this.selectedExamDetailService.setCandidateGuid(guid);
                  bookingRequestData['candidate']['id'] = guid;

                  if (cardDetails) { cardDetails['candidateId'] = guid; }
                  if (voucherDetails) { voucherDetails['candidateId'] = guid; }

                  return this.authorizePayment(voucherDetails);
                }),
                catchError((err) => {
                  return throwError(() => new Error('Candidate Error'));
                }),
                mergeMap((authResponse) => {
                  if (authResponse.isRequired && authResponse.isAuthorized) {
                    bookingRequestData.payments.push(authResponse.transactionId);
                    this.voucherAuthId = authResponse.transactionId;
                  } else if (authResponse.isRequired && !authResponse.isAuthorized) {
                    return throwError(() => new Error('Voucher Authorization Error'));
                  }
                  return this.authorizePayment(cardDetails);
                }),
                catchError((err) => {
                  return throwError(() => new Error('Voucher Authorization Error'));
                }),
                mergeMap((authResponse) => {
                  if (authResponse.isRequired && authResponse.isAuthorized) {
                    bookingRequestData.payments.push(authResponse.transactionId);
                    this.authId = authResponse.transactionId;
                  } else if (authResponse.isRequired && !authResponse.isAuthorized) {
                    return throwError(() => new Error('Credit Card Authorization Error'));
                  }
                  return this.createBooking(bookingRequestData);
                }),
                catchError((err) => {
                  if (err.message === 'Credit Card Authorization Error') {
                    return throwError(() => err);
                  } else {
                    return throwError(() => new Error('Booking Error'));
                  }
                }))
                .subscribe({
                  next: (response: any) => {
                    if (response.id) {
                      let bookingId: any = response.id;
                      if (response.tests.length > 0) {
                        this.bookingResult = response;
                        if (response.tests[0].sessions[0] != null && response.tests[0].sessions[0] != undefined) {
                          msgResp = {
                            message: "confirmationComponent.confirmationText",
                            status: true,
                            confirmationNumber: response.tests[0].sessions[0].confirmationNumber,
                            response: response
                          };

                          if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                            this.postConsents(response.tests).subscribe(() => { });
                          }

                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                          }
                        }
                      }
                      observer.next(msgResp);
                      observer.complete();
                    } else {
                      throwError(() => new Error('Booking Error'));
                    }
                  },
                  error: (err) => {
                    if (err.message === 'Candidate Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Voucher Authorization Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.paymentErrorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Credit Card Authorization Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.paymentErrorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Booking Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    }
                    if (this.voucherAuthId) {
                      const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
                      this.metricApiService.log(LogLevel.Debug, `Rolling back voucher transaction due to ${err}\nHold: ${holdId}`);
                      this.paymentService.rollBackTransaction(this.voucherAuthId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          if (!resp.isCancelled) {
                            msgResp = {
                              message: "schedulePaymentComponent.errorMessage",
                              status: false,
                              confirmationNumber: "Error",
                              response: ""
                            };
                            observer.next(msgResp);
                          }
                        }
                      });
                    }
                    if (this.authId) {
                      const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
                      this.metricApiService.log(LogLevel.Debug, `Rolling back payment transaction due to ${err}\nHold: ${holdId}`);
                      this.paymentService.rollBackTransaction(this.authId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          if (!resp.isCancelled) {
                            msgResp = {
                              message: "schedulePaymentComponent.errorMessage",
                              status: false,
                              confirmationNumber: "Error",
                              response: ""
                            };
                            observer.next(msgResp);
                          }
                        }
                      });
                    }
                    observer.complete();
                  }
                });
            } else if (!!candidateGuid) {
              bookingRequestData['candidate']['id'] = candidateGuid;

              if (cardDetails) { cardDetails['candidateId'] = candidateGuid; }
              if (voucherDetails) { voucherDetails['candidateId'] = candidateGuid; }

              this.authorizePayment(voucherDetails)
                .pipe(mergeMap((authResponse: any) => {
                  if (authResponse.isRequired && authResponse.isAuthorized) {
                    bookingRequestData.payments.push(authResponse.transactionId);
                    this.voucherAuthId = authResponse.transactionId;
                  } else if (authResponse.isRequired && !authResponse.isAuthorized) {
                    return throwError(() => new Error('Voucher Authorization Error'));
                  }
                  return this.authorizePayment(cardDetails);
                }),
                catchError((err) => {
                  return throwError(() => new Error('Voucher Authorization Error'));
                }),
                mergeMap((authResponse: any) => {
                  if (authResponse.isRequired && authResponse.isAuthorized) {
                    bookingRequestData.payments.push(authResponse.transactionId);
                    this.authId = authResponse.transactionId;
                  } else if (authResponse.isRequired && !authResponse.isAuthorized) {
                    return throwError(() => new Error('Credit Card Authorization Error'));
                  }
                  return this.createBooking(bookingRequestData);
                }),
                catchError((err) => {
                  if (err.message === 'Credit Card Authorization Error') {
                    return throwError(() => err);
                  } else {
                    return throwError(() => new Error('Booking Error'));
                  }
                }))
                .subscribe({
                  next: (response: any) => {
                    if (response.id) {
                      let bookingId: any = response.id;
                      if (response.tests.length > 0) {
                        this.bookingResult = response;
                        if (response.tests[0].sessions[0] != null && response.tests[0].sessions[0] != undefined) {
                          msgResp = {
                            message: "confirmationComponent.confirmationText",
                            status: true,
                            confirmationNumber: response.tests[0].sessions[0].confirmationNumber,
                            response: response
                          };

                          if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                            this.postConsents(response.tests).subscribe(() => { });
                          }


                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                          }
                        }
                      }
                      observer.next(msgResp);
                      observer.complete();
                    } else {
                      throwError(() => new Error('Booking Error'));
                    }
                  },
                  error: (err) => {
                    if (err.message === 'Candidate Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Voucher Authorization Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.paymentErrorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Credit Card Authorization Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.paymentErrorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Booking Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    }
                    if (this.voucherAuthId) {
                      const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
                      this.metricApiService.log(LogLevel.Debug, `Rolling back voucher transaction due to ${err}\nHold: ${holdId}`);
                      this.paymentService.rollBackTransaction(this.voucherAuthId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          if (!resp.isCancelled) {
                            msgResp = {
                              message: "schedulePaymentComponent.errorMessage",
                              status: false,
                              confirmationNumber: "Error",
                              response: ""
                            };
                            observer.next(msgResp);
                          }
                        }
                      });
                    }
                    if (this.authId) {
                      const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
                      this.metricApiService.log(LogLevel.Debug, `Rolling back payment transaction due to ${err}\nHold: ${holdId}`);
                      this.paymentService.rollBackTransaction(this.authId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          if (!resp.isCancelled) {
                            msgResp = {
                              message: "schedulePaymentComponent.errorMessage",
                              status: false,
                              confirmationNumber: "Error",
                              response: ""
                            };
                            observer.next(msgResp);
                          }
                        }
                      });
                    }
                    observer.complete();
                  }
                });
            } else {
              this.createCandidate(candidateRequestData)
                .pipe(mergeMap((guid) => {
                  this.selectedExamDetailService.setCandidateGuid(guid);
                  bookingRequestData['candidate']['id'] = guid;

                  if (cardDetails) { cardDetails['candidateId'] = guid; }
                  if (voucherDetails) { voucherDetails['candidateId'] = guid; }
                  if (guid) {
                    return this.authorizePayment(voucherDetails);
                  }
                }),
                catchError((err) => {
                  return throwError(() => new Error('Candidate Error'));
                }),
                mergeMap((authResponse: any) => {
                  if (authResponse.isRequired && authResponse.isAuthorized) {
                    bookingRequestData.payments.push(authResponse.transactionId);
                    this.voucherAuthId = authResponse.transactionId;
                  } else if (authResponse.isRequired && !authResponse.isAuthorized) {
                    return throwError(() => new Error('Voucher Authorization Error'));
                  }
                  if (bookingRequestData['candidate']['id']) {
                    return this.authorizePayment(cardDetails);
                  }
                }),
                catchError((err) => {
                  return throwError(() => new Error('Voucher Authorization Error'));
                }),
                mergeMap((authResponse: any) => {
                  if (authResponse.isRequired && authResponse.isAuthorized) {
                    bookingRequestData.payments.push(authResponse.transactionId);
                    this.authId = authResponse.transactionId;
                  } else if (authResponse.isRequired && !authResponse.isAuthorized) {
                    return throwError(() => new Error('Credit Card Authorization Error'));
                  }
                  return this.createBooking(bookingRequestData);
                }),
                catchError((err) => {
                  if (err.message === 'Credit Card Authorization Error') {
                    return throwError(() => err);
                  } else {
                    return throwError(() => new Error('Booking Error'));
                  }
                }))
                .subscribe({
                  next: (response: any) => {
                    if (response.id) {
                      let bookingId: any = response.id;
                      if (response.tests.length > 0) {
                        this.bookingResult = response;
                        if (response.tests[0].sessions[0] != null && response.tests[0].sessions[0] != undefined) {
                          msgResp = {
                            message: "confirmationComponent.confirmationText",
                            status: true,
                            confirmationNumber: response.tests[0].sessions[0].confirmationNumber,
                            response: response
                          };

                          if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule || this.selectedExamDetailService.isCustomAgreementChecked) {
                            this.postConsents(response.tests).subscribe(() => { });
                          }


                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], bookingId, "Schedule");
                          }
                        }
                      }
                      observer.next(msgResp);
                      observer.complete();
                    } else {
                      throwError(() => new Error('Booking Error'));
                    }
                  },
                  error: (err) => {
                    if (err.message === 'Candidate Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Voucher Authorization Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.paymentErrorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Credit Card Authorization Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.paymentErrorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else if (err.message === 'Booking Error') {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    } else {
                      msgResp = {
                        message: "schedulePaymentComponent.errorMessage",
                        status: false,
                        confirmationNumber: "Error",
                        response: ""
                      };
                      observer.next(msgResp);
                    }
                    if (this.voucherAuthId) {
                      const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
                      this.metricApiService.log(LogLevel.Debug, `Rolling back voucher transaction due to ${err}\nHold: ${holdId}`);
                      this.paymentService.rollBackTransaction(this.voucherAuthId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          if (!resp.isCancelled) {
                            msgResp = {
                              message: "schedulePaymentComponent.errorMessage",
                              status: false,
                              confirmationNumber: "Error",
                              response: ""
                            };
                            observer.next(msgResp);
                          }
                        }
                      });
                    }
                    if (this.authId) {
                      const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
                      this.metricApiService.log(LogLevel.Debug, `Rolling back payment transaction due to ${err}\nHold: ${holdId}`);
                      this.paymentService.rollBackTransaction(this.authId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          if (!resp.isCancelled) {
                            msgResp = {
                              message: "schedulePaymentComponent.errorMessage",
                              status: false,
                              confirmationNumber: "Error",
                              response: ""
                            };
                            observer.next(msgResp);
                          }
                        }
                      });
                    }
                    observer.complete();
                  }
                });
            }
          } else {
            const holdId = bookingPostRequestData['tests'][0]["sessions"][0].hold.id;
            this.metricApiService.log(LogLevel.Error, `Validate hold call returned expired when checking for hold ${holdId}`);
            this.timerService.expireTimer();
          }
        });
      }
    });
  }

  public getCandidateRequestData(candidate: Candidate) {

    // Remove unused defined attribute from coming candidate detail dictionary to avoid unnecessary key loop .
    let candidateRequestData: Candidate = new Candidate();
    candidateRequestData.Values = [];

    if (candidate && candidate.defaultInitialDetails) {
      // to make sure all demographcis from CMS are posted along with updated demographics
      var defaultDetailKeys = candidate.defaultInitialDetails._keys;
      for (var i = 0; i < candidate.defaultInitialDetails._keys.length; i++) {
        // if a key is not in default initial details and either the user bypassed the demographics page or the key is neither a generic key nor a fieldmap
        if (!candidate.details.containsKey(defaultDetailKeys[i].toUpperCase()) && (!candidate.demographicTemplateKeys || !candidate.demographicTemplateKeys.containsKey(defaultDetailKeys[i].toLowerCase()))) {
          candidate.details.add(defaultDetailKeys[i].toUpperCase(), candidate.defaultInitialDetails[defaultDetailKeys[i]]);
          if (candidate.defaultInitialDetails['birth_date'] && candidate.defaultInitialDetails['birth_date']['jsdate']) {
            candidate.details.add('birth_date', new Date(candidate.defaultInitialDetails['birth_date']['jsdate']).toISOString());
          }
        }
      }
    }

    let requestData: any = this.removeKeyValueAttributes(candidate.details,
      ['_keys', '_values', 'add', 'remove', 'keys', 'values', 'containsKey', 'getPostdataUrl']);
    for (const key in requestData) {
      if ((candidate.details[key] != null && candidate.details[key] != undefined && candidate.details[key] != '')) {

        // change all datepicker values formatting
        const formItem = !!this.dynamicInputCtrls && !!this.dynamicInputCtrls.length ? this.dynamicInputCtrls.find(d => d.key.toLowerCase() === key.toLowerCase()) : null;
        if (!!formItem && formItem.controlType === 'datepicker') {
          candidate.details[key] = this.getStringfromDatepickerValue(candidate, key)
        }

        switch (key.toLocaleLowerCase().trim()) {

          case "first_name":
          case "firstname_rsa":
            candidateRequestData.FirstName = candidate.details[key];
            break;
          case "middle_name":
            candidateRequestData.MiddleName = candidate.details[key];
            break;
          case "last_name":
          case "lastname_rsa":
            candidateRequestData.LastName = candidate.details[key];
            break;
          case "email":
            candidateRequestData.EmailAddress = candidate.details[key];
            break;
          case "address1":
            candidateRequestData.AddressLine1 = candidate.details[key];
            break;
          case "address2":
            candidateRequestData.AddressLine2 = candidate.details[key];
            break;
          case "address3":
            candidateRequestData.AddressLine3 = candidate.details[key];
            break;
          case "address4":
            candidateRequestData.AddressLine4 = candidate.details[key];
            break;
          case "city":
            candidateRequestData.City = candidate.details[key];
            break;
          case "state":
            candidateRequestData.StateProvince = candidate.details[key];
            break;
          case "zipcode":
            candidateRequestData.PostalCode = candidate.details[key];
            break;
          case "country":
            candidateRequestData.Country = candidate.details[key];
            break;
          case "h_phone":
            candidateRequestData.PrimaryPhoneNumber = candidate.details[key];
            break;
          case "h_phone_ext":
            candidateRequestData.PrimaryPhoneExtension = candidate.details[key];
            break;
          case "w_phone":
            candidateRequestData.SecondaryPhoneNumber = candidate.details[key];
            break;
          case "w_phone_ext":
            candidateRequestData.SecondaryPhoneExtension = candidate.details[key];
            break;
          case "f_phone":
            candidateRequestData.FaxNumber = candidate.details[key];
            break;
          case "birth_date":
            // Change birth_date formatting if it wasn't already changed
            if (!!formItem && formItem.controlType === 'datepicker') {
              candidateRequestData.BirthDate = candidate.details[key]
            } else {
              candidateRequestData.BirthDate = this.getStringfromDatepickerValue(candidate, key);
            }
            break;
          case "suffix":
            candidateRequestData.Suffix = candidate.details[key];
            break;
          default:
            // if any key does not exists in case then it will push into values properties of model.
            let otherValues: any = { "Name": key.toLocaleLowerCase().trim(), "Value": candidate.details[key] }
            candidateRequestData.Values.push(otherValues);
            break;
        }
      }
    }

    this.selectedExamDetailService.client.subscribe(client => {
      candidateRequestData.clientCode = client ? client.code : '';
    });

    this.selectedExamDetailService.program.subscribe(program => {
      candidateRequestData.programCode = program ? program.code : '';
    });

    this.selectedExamDetailService.selectedAppointmentDetails.subscribe(appt => {
      candidateRequestData.examCode = appt && appt[0].exam.code;
    });

    return candidateRequestData;
  }

  // changes value to T00 and moves it out of the jsdate object if we need to
  private getStringfromDatepickerValue(candidate: Candidate, key: string): string {
    if (candidate.details[key].jsdate) {
      return moment(candidate.details[key].jsdate).format('YYYY-MM-DDTHH:mm:ss');
    } else if (candidate.details[key]) {
      return moment(candidate.details[key]).format('YYYY-MM-DDTHH:mm:ss');
    }
  }

  // Remove Key value attribute from the object and the serialize object to pass Candidate api call
  private removeKeyValueAttributes(inputObj: any, keys: any) {
    var dup = {};
    for (let key in inputObj) {
      if (keys.indexOf(key) == -1) {
        dup[key] = inputObj[key];
      }
    }
    return dup;
  }

  private getFirstOrDefault(apiResponse: any): any {
    let resultArray: any = (apiResponse.results !== undefined) ? apiResponse.results : apiResponse;
    return resultArray;
  }

  private getResultArray(apiResponse: any): any[] {
    let resultArray = apiResponse;
    return resultArray || [];
  }

  private applyEligibilitySort(eligibilities: any[]): any[] {
    let returnEligibility: any[] = [];

    eligibilities.forEach((eligib) => {
      if (eligib.testingAccommodations != null && eligib.testingAccommodations != undefined) {
        if (eligib.testingAccommodations.length > 0) {
          returnEligibility.push(eligib);
        }
      }
    });

    let filterEligibility = eligibilities.filter((eligbFilter: any) => { return eligbFilter.testingAccommodations == null || eligbFilter.testingAccommodations == undefined || eligbFilter.testingAccommodations.length == 0 });

    if (filterEligibility != null && filterEligibility != undefined) {
      returnEligibility = returnEligibility.concat(filterEligibility);
    }

    return returnEligibility;
  }

  public updateCandidate(guid: string, requestBody: Candidate): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.put(this.bootstrapParams.RegistrationRootApiUrl + 'api/v1/Candidates/' + guid,
        requestBody, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            if (rNArray.id != undefined && rNArray.id != null) {
              observer.next(rNArray.id);
              observer.complete();
            }
          },
          error: (err) => {
            observer.error(err);
          }
        });
    });
  }

  private createBooking(bookingRequestData: any): Observable<any> {
    //boolean variable with true value to send to API to trigger email service
    bookingRequestData['sendConfirmation'] = true;
    const holdId = bookingRequestData['tests'][0]["sessions"][0].hold.id;
    this.metricApiService.log(LogLevel.Debug, `Attempting booking creation.\nHold: ${holdId}`);
    return new Observable((observer: Observer<any>) => {
      this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v1/bookings', bookingRequestData, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            this.metricApiService.log(LogLevel.Debug, `Booking creation completed.\nHold: ${holdId}`);
            observer.next(rNArray);
            observer.complete();
          },
          error: (err) => {
            this.metricApiService.log(LogLevel.Debug, `Error creating booking: ${JSON.stringify(err)}\nHold: ${holdId}`);
            observer.error('booking failed!');
            observer.complete();
          }
        });
    })

  }

  private postConsents(tests: any): Observable<any> {
    let body: any = {
      "consent": {}
    }

    if (this.selectedExamDetailService.isBiometricConsentCheckedSchedule) {
      body.consent["Biometric"] = ['web'];
    }
    if (this.selectedExamDetailService.isCustomAgreementChecked) {
      body.consent[this.selectedExamDetailService.customAgreementName] = ['web'];
    }

    return new Observable((observer: Observer<any>) => {
      tests.forEach((test: any) => {
        this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + test.sessions[0].id + '/consents', body, this.bootstrapParams.Token)
          .pipe(map(this.getFirstOrDefault))
          .subscribe(() => {
            observer.next(null);
            observer.complete();
          });
      });
    });
  }

  private postBiometrics(tests: any): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      tests.forEach((test: any) => {
        this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + test.sessions[0].id + '/consents', {
          "consent": {
            "Biometric": ['web']
          }
        }, this.bootstrapParams.Token)
          .pipe(map(this.getFirstOrDefault))
          .subscribe(() => {
            observer.next(null);
            observer.complete();
          });
      })

    });
  }

  private postConfirmation(tests: any[]): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let apptIds: string[] = [];
      tests.forEach((test) => {
        apptIds = apptIds.concat(test.sessions.map((s: any) => s.id));
      });
      this.messagingApiService.sendConfirmation(apptIds)
        .pipe(map(this.getFirstOrDefault))
        .subscribe(() => {
          observer.next(null);
          observer.complete();
        });

    });
  }

  public createCandidate(requestBody: Candidate): Observable<any> {

    return new Observable((observer: Observer<any>) => {
      this.httpService.post(this.bootstrapParams.RegistrationRootApiUrl + 'api/v1/Candidates/',
        requestBody, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            if (rNArray.id != undefined && rNArray.id != null) {
              observer.next(rNArray.id);
              observer.complete();
            }
          },
          error: (err) => {
            //Need To Implement Global Error Handling (Remove Code Below) // In case candiate call fail
            observer.next(null);
            observer.complete();
          }
        });
    });
  }

  private authorizePayment(paymentDetails: any): Observable<any> {
    if (!paymentDetails) {
      return new Observable((observer: Observer<any>) => {
        observer.next({
          // payment details not being processed: not required
          // payment details not validated: not authorized
          isAuthorized: false,
          isRequired: false
        })
      });
    }

    return new Observable((observer: Observer<any>) => {
      this.paymentService.validateCardDetails(paymentDetails).subscribe((response) => {
        if (response.isAuthorized) {
          // payment details is being processed: required
          // payment details validated successfully: authorized
          response.isRequired = true;
          observer.next(response);
          observer.complete();
        } else {
          observer.next({
            // payment details is being processed: required
            // payment details not validated successfully: not authorized
            isAuthorized: false,
            isRequired: true
          });
          observer.complete();
        }
      })
    });
  }
}
